<template>
  <SAIPageWrapper :pad-x="false" :pad-y="false">
    <FinanceTabs />
    <div class="column q-gutter-y-lg q-px-md q-pt-lg">
      <div>
        <SAITitle text-align="left">
          Let’s work out your car lease repayments
        </SAITitle>
        <p class="q-pt-sm">
          Fill in the details below to find out how much you could potentially
          save with a Novated Lease.
        </p>
      </div>

      <SAIMessage
        variant="negative"
        class="q-mt-md"
        v-if="isMaxAllowedTermKmsExceeded"
      >
        <slot>
          For a {{ leaseTerm }} year lease term the maximum total kilometres is
          {{ maxAllowedTotalKms }} per year
        </slot>
      </SAIMessage>

      <SAIMessage
        variant="negative"
        class="q-mt-md"
        v-if="isMaxAllowedCarCostExceeded"
      >
        <slot> The maximum car price is {{ carValueMax }} </slot>
      </SAIMessage>
      <SAIFormError
        class="q-mt-md"
        v-else-if="errors.customerOptions.$error && errors.$dirty"
      />

      <div>
        <SAIFormLabel class="text-center">
          Gross Annual Salary
          <SAIInformationPopup>
            <p>
              This is your gross annual salary excluding superannuation and
              other salary packaging deductions.
            </p>
          </SAIInformationPopup>
        </SAIFormLabel>
        <SAICurrencyInput
          :error="errors.customerOptions.annualSalary.$error"
          :disable="disable"
          :value="annualSalary"
          @input="$emit('update:annual-salary', $event)"
        />
        <SAISlider
          :disable="disable"
          :labels="[
            '$0k',
            '$50k',
            '$100k',
            '$150k',
            '$200k',
            '$250k',
            '$300k+',
          ]"
          :value="annualSalary"
          :min="annualSalaryMin"
          :max="annualSalaryMax"
          :step="10000"
          snap
          @change="$emit('update:annual-salary', $event)"
        />
      </div>

      <div>
        <SAIFormLabel class="text-center">Car Condition</SAIFormLabel>
        <NewUsedList
          :error="errors.customerOptions.vehicleCondition.$error"
          :disable="disable"
          :value="vehicleCondition"
          @input="$emit('update:vehicle-condition', $event)"
        />
      </div>

      <div class="full-width">
        <SAIFormLabel class="text-center">Car Body Type</SAIFormLabel>
        <div class="row scroll no-wrap">
          <div
            v-for="option in bodyTypeOptions"
            :key="option.name"
            class="q-mr-sm"
          >
            <SAICardToggle
              :error="errors.customerOptions.bodyType.$error"
              :disable="disable"
              class="q-py-md q-px-lg items-center column"
              :active="option.name === bodyType"
              @click="$emit('update:body-type', option.name)"
            >
              <QIcon :name="option.icon" size="36px" class="q-mb-xs" />
              <span class="text-weight-medium text-no-wrap text-label">
                {{ option.name }}
              </span>
            </SAICardToggle>
          </div>
        </div>
      </div>

      <div>
        <SAIFormLabel class="text-center">
          Estimated Car Cost
          <SAIInformationPopup>
            <p>
              Enter the estimated on-road price of the car you’re interested in.
              Also include the cost of any after-market accessories e.g. tow
              bar, window tinting, protection pack, dashcam.
            </p>
          </SAIInformationPopup>
        </SAIFormLabel>
        <SAICurrencyInput
          :error="errors.customerOptions.carValue.$error"
          :disable="disable"
          :value="carValue"
          @input="$emit('update:car-value', $event)"
        />
        <SAISlider
          :disable="disable"
          :labels="['$10k', '$40k', '$70k', '$100k', '$130k', '$160k', '$170k']"
          :value="carValue"
          :min="carValueMin"
          :max="carValueMax"
          :step="10000"
          snap
          @change="$emit('update:car-value', $event)"
        />
      </div>

      <div class="full-width">
        <SAIFormLabel class="text-center">
          Lease Term (years)
          <SAIInformationPopup>
            <p>Select how long you would like to lease the vehicle.</p>
          </SAIInformationPopup>
        </SAIFormLabel>
        <LeaseTermSelect
          :error="errors.customerOptions.leaseTerm.$error"
          :disable="disable"
          :value="leaseTerm"
          @input="$emit('update:lease-term', $event)"
        />
      </div>

      <div>
        <SAIFormLabel class="text-center">
          Estimated Annual KMs
          <SAIInformationPopup>
            <p>
              Enter an estimate of the number of kilometres you anticipate
              driving per year.
            </p>
          </SAIInformationPopup>
        </SAIFormLabel>
        <SAICurrencyInput
          :error="errors.customerOptions.kmsPerYear.$error"
          :disable="disable"
          :value="kmsPerYear"
          prefix=""
          @input="$emit('update:kmsPerYear', $event)"
        />
        <SAISlider
          :disable="disable"
          :labels="maxKmsPerYearLabels"
          :value="kmsPerYear"
          :min="minKmsPerYear"
          :max="maxKmsPerYear"
          :step="maxKmsPerYearStep"
          snap
          @input="$emit('update:kmsPerYear', $event)"
        />
      </div>
    </div>

    <div class="q-pa-lg justify-center row">
      <SAIBtn
        :disable="disable"
        padding="12px xl"
        label="Calculate"
        @click="$emit('calculation:show')"
      />
    </div>

    <BudgetSummary
      v-if="showCalculation"
      class="sai-print-avoid-break-inside q-mb-lg q-mt-xl"
      :loading="financeLoading"
      :disable="disable"
      :action-label="continueLabel"
      :action-loading="applicationSaving"
      :action-disable="financeLoading"
      :novated="novated"
      :calculations="summaryCalculations"
      :take-home="takeHome"
      :lease-term-years="leaseTerm"
      :frequency="calculationFrequency"
      :budget-comparison-view="budgetComparisonView"
      :budget-lease-type-view="budgetLeaseTypeView"
      @continue="$emit('continue')"
      @update:budget-comparison="$emit('update:budget-comparison', $event)"
      @update:budget-lease-type="$emit('update:budget-lease-type', $event)"
      @update:frequency="$emit('update:frequency', $event)"
    />

    <BudgetTerms
      class="q-px-md q-mb-lg"
      :calculator-assumptions-href="calculatorAssumptionsHref"
    />

    <QSeparator />

    <MoreInformation
      class="q-pb-lg sai-print-avoid-break-inside"
      :contact-number="contactNumber"
    />
  </SAIPageWrapper>
</template>
<script>
import {
  SAIBtn,
  SAICardToggle,
  SAICurrencyInput,
  SAIFormError,
  SAIFormLabel,
  SAIInformationPopup,
  SAIMessage,
  SAIPageWrapper,
  SAISlider,
  SAITitle,
} from 'ecx.saigon.ui.common';
import { QIcon, QSeparator } from 'quasar';
import { formatNumber } from '../../../utils/number-formatter';

export default {
  name: 'ApplicationCalculator',
  data() {
    return {
      annualSalaryMin: this.$Constants.ANNUAL_SALARY_MIN,
      annualSalaryMax: this.$Constants.ANNUAL_SALARY_MAX,
      carValueMin: this.$Constants.CAR_VALUE_MIN,
      carValueMax: this.$Constants.CAR_VALUE_MAX,
      leaseTermOptions: this.$Constants.LEASE_TERM_OPTIONS,
      calculationFrequencyOptions: Object.values(
        this.$Constants.CALCULATION_FREQUENCIES
      ),
    };
  },
  computed: {
    isMaxAllowedTermKmsExceeded() {
      const kmsPerYear = this.errors?.customerOptions?.kmsPerYear || {
        termKmsCheck: true,
      };
      return !kmsPerYear.termKmsCheck;
    },
    maxAllowedTotalKms() {
      return this.leaseTerm > 0
        ? formatNumber(this.$Constants.MAX_ALLOWED_TERM_KMS / this.leaseTerm)
        : '';
    },
    isMaxAllowedCarCostExceeded() {
      const carValue = this.errors?.customerOptions?.carValue || {
        carValueCheck: true,
      };
      return !carValue.carValueCheck;
    },
  },
  props: {
    minKmsPerYear: Number,
    maxKmsPerYear: Number,
    maxKmsPerYearStep: Number,
    maxKmsPerYearLabels: {
      type: Array,
      default: () => [],
    },
    contactNumber: String,
    financeLoading: Boolean,
    continueLabel: String,
    budgetComparisonView: String,
    budgetLeaseTypeView: String,
    disable: Boolean,
    kmsPerYear: Number,
    showCalculation: Boolean,
    annualSalary: Number,
    carValue: Number,
    leaseTerm: Number,
    bodyType: String,
    calculatorAssumptionsHref: String,
    vehicleCondition: String,
    vehicleConditionList: {
      type: Array,
    },
    bodyTypeOptions: {
      type: Array,
      default: () => [],
    },
    calculationFrequency: {
      type: String,
      required: true,
    },
    novated: {
      type: Object,
      default: () => ({}),
    },
    summaryCalculations: {
      type: Array,
      default: () => [],
    },
    takeHome: {
      type: Object,
      default: () => ({}),
    },
    applicationSaving: Boolean,
    displayFormError: Boolean,
    errors: {
      type: Object,
      default: () => ({
        customerOptions: {
          annualSalary: {},
          vehicleCondition: {},
          bodyType: {},
          carValue: {},
          leaseTerm: {},
          kmsPerYear: {},
        },
      }),
    },
  },
  components: {
    QIcon,
    SAITitle,
    SAISlider,
    SAIBtn,
    QSeparator,
    SAIPageWrapper,
    SAIInformationPopup,
    SAICurrencyInput,
    SAICardToggle,
    SAIFormLabel,
    SAIFormError,
    SAIMessage,
    BudgetTerms: () => import('../../atom/budget-terms'),
    LeaseTermSelect: () => import('../../molecule/lease-term-select'),
    MoreInformation: () => import('../../organism/more-information'),
    BudgetSummary: () => import('../../organism/budget-summary'),
    FinanceTabs: () => import('../../../widgets/finance-tabs'),
    NewUsedList: () => import('../../atom/new-used-list'),
  },
};
</script>
